import { CATEGORY_LABELS, TICKET_NUMBER_PATTERN, type FeedbackInput, type Status } from '@helpdesk/shared';
import { useMutation, useQuery } from '@tanstack/react-query';
import { BookOpen, CircleCheck, Inbox, Plus, Search } from 'lucide-react';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router';
import { FeedbackForm } from '../components/FeedbackForm.tsx';
import { StatusBadge } from '../components/ticket-badges.tsx';
import { Button, ButtonLink } from '../components/ui/button.tsx';
import { Field, Input } from '../components/ui/form.tsx';
import { Alert, Card, PageHeader, Spinner } from '../components/ui/misc.tsx';
import { api, ApiError } from '../lib/api.ts';
import { formatDateTime } from '../lib/format.ts';
import { usePageTitle } from '../lib/navigation.ts';
import { useSession } from '../lib/session.ts';
import type { PublicStatus } from '../lib/types.ts';
import { useHashToken } from '../lib/useHashToken.ts';

// ── Home ────────────────────────────────────────────────────────────

export function HomePage() {
  const { user, isIt, isLoading } = useSession();
  usePageTitle(null);
  if (isLoading) return <Spinner />;
  if (user) return <Navigate to={isIt ? '/queue' : '/tickets'} replace />;

  const tiles = [
    {
      to: '/new',
      icon: Plus,
      title: 'Report a problem',
      text: 'Tell IT what’s wrong. No account needed.',
      primary: true,
    },
    {
      to: '/sign-in',
      icon: Inbox,
      title: 'Follow my tickets',
      text: 'Sign in with your email to see progress and reply.',
    },
    {
      to: '/status',
      icon: Search,
      title: 'Check a ticket’s status',
      text: 'Have a ticket number? Look it up.',
    },
    { to: '/help', icon: BookOpen, title: 'Help articles', text: 'Quick fixes for common problems.' },
  ];

  return (
    <div className="mx-auto max-w-3xl">
      <div className="py-6 text-center sm:py-12">
        <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">How can IT help you today?</h1>
        <p className="mt-3 text-lg text-muted">
          The IT helpdesk for the Navrongo-Bolgatanga Catholic Diocesan Development Organization.
        </p>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        {tiles.map(({ to, icon: Icon, title, text, primary }) => (
          <Link
            key={to}
            to={to}
            className={`group flex gap-4 rounded-xl border p-5 shadow-card transition-colors ${
              primary
                ? 'border-brand bg-brand text-on-brand hover:bg-brand-hover sm:col-span-2'
                : 'border-line bg-surface hover:border-brand'
            }`}
          >
            <span
              className={`flex size-11 shrink-0 items-center justify-center rounded-lg ${
                primary ? 'bg-white/15' : 'bg-brand-soft text-brand'
              }`}
            >
              <Icon className="size-5" aria-hidden />
            </span>
            <span>
              <span className="block font-semibold">{title}</span>
              <span className={`block text-sm ${primary ? 'opacity-85' : 'text-muted'}`}>{text}</span>
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
}

// ── Public status lookup ────────────────────────────────────────────

const STEPS: { status: Status[]; label: string }[] = [
  { status: ['open'], label: 'Received' },
  { status: ['in_progress', 'waiting'], label: 'Being worked on' },
  { status: ['resolved', 'closed'], label: 'Resolved' },
];

export function StatusPage() {
  const [params, setParams] = useSearchParams();
  const number = (params.get('number') ?? '').toUpperCase();
  const [input, setInput] = useState(number);
  const [inputError, setInputError] = useState<string>();

  const query = useQuery({
    queryKey: ['public-status', number],
    queryFn: () => api.get<PublicStatus>(`/api/public/status/${encodeURIComponent(number)}`),
    enabled: TICKET_NUMBER_PATTERN.test(number),
    retry: false,
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const value = input.trim().toUpperCase();
    if (!TICKET_NUMBER_PATTERN.test(value)) return setInputError('Ticket numbers look like NAB-2026-0014.');
    setInputError(undefined);
    setParams({ number: value });
  };

  const t = query.data;
  const step = t ? STEPS.findIndex((s) => s.status.includes(t.status)) : -1;

  return (
    <div className="mx-auto max-w-xl">
      <PageHeader
        back="/"
        title="Check a ticket’s status"
        description="Enter the ticket number from your confirmation."
      />
      <Card className="p-5">
        <form onSubmit={submit} noValidate className="flex flex-col gap-3 sm:flex-row sm:items-start">
          <Field label="Ticket number" error={inputError} className="flex-1">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="NAB-2026-0014"
              autoCapitalize="characters"
              className="font-mono uppercase"
            />
          </Field>
          <Button type="submit" className="sm:mt-7" loading={query.isFetching}>
            Look up
          </Button>
        </form>
      </Card>

      {query.isError && (
        <Alert tone="error" className="mt-4">
          {query.error.message}
        </Alert>
      )}
      {t && (
        <Card className="mt-4 p-5">
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge status={t.status} forStaff />
            <span className="font-mono text-sm text-muted">{t.number}</span>
          </div>
          <p className="mt-2 text-lg font-semibold">{t.subject}</p>
          <p className="text-sm text-muted">
            {CATEGORY_LABELS[t.category]} · reported {formatDateTime(t.createdAt)}
          </p>
          <ol className="mt-6 flex items-start">
            {STEPS.map((s, i) => (
              <li key={s.label} className="flex flex-1 flex-col items-center text-center">
                <div className="flex w-full items-center">
                  <span
                    className={`h-0.5 flex-1 ${i === 0 ? 'bg-transparent' : i <= step ? 'bg-brand' : 'bg-line'}`}
                  />
                  <span
                    className={`flex size-8 items-center justify-center rounded-full text-sm font-bold ${
                      i <= step ? 'bg-brand text-on-brand' : 'bg-surface-2 text-muted'
                    }`}
                  >
                    {i < step || (i === step && step === 2) ? (
                      <CircleCheck className="size-4" aria-hidden />
                    ) : (
                      i + 1
                    )}
                  </span>
                  <span
                    className={`h-0.5 flex-1 ${i === STEPS.length - 1 ? 'bg-transparent' : i < step ? 'bg-brand' : 'bg-line'}`}
                  />
                </div>
                <span className={`mt-2 text-xs font-medium ${i <= step ? 'text-text' : 'text-muted'}`}>
                  {s.label}
                </span>
              </li>
            ))}
          </ol>
          {t.status === 'open' && (
            <p className="mt-5 text-sm text-muted">IT aims to respond {t.estimatedResponse}.</p>
          )}
          <p className="mt-5 border-t border-line pt-4 text-sm text-muted">
            Want to see messages and reply?{' '}
            <Link
              to={`/sign-in?next=/tickets/${t.number}`}
              className="font-medium text-brand hover:underline"
            >
              Sign in with your email
            </Link>
            .
          </p>
        </Card>
      )}
    </div>
  );
}

// ── Feedback via emailed link ───────────────────────────────────────

export function FeedbackPage() {
  // Read before useHashToken tidies the address bar.
  const [tapped] = useState(() => {
    const n = Number(new URLSearchParams(window.location.search).get('rate'));
    return n >= 1 && n <= 5 ? n : null;
  });
  const token = useHashToken();

  const info = useQuery({
    queryKey: ['feedback-link'],
    queryFn: () =>
      api.post<{ number: string; subject: string; category: string; satisfaction: number | null }>(
        '/api/public/feedback/lookup',
        { token },
      ),
    enabled: !!token,
    retry: false,
    staleTime: Infinity,
  });
  const submit = useMutation({
    mutationFn: (input: FeedbackInput) => api.post('/api/public/feedback/submit', { ...input, token }),
  });
  // A star tapped in the email is saved straight away; the details are optional.
  const quick = useMutation({
    mutationFn: (satisfaction: number) => api.post('/api/public/feedback/submit', { satisfaction, token }),
  });
  const quickStarted = useRef(false);
  const alreadyRated = info.data?.satisfaction ?? null;
  useEffect(() => {
    if (!tapped || !info.data || alreadyRated || quickStarted.current) return;
    quickStarted.current = true;
    quick.mutate(tapped);
  }, [tapped, info.data, alreadyRated, quick]);
  const rated = quick.isSuccess ? tapped : alreadyRated;

  if (!token) {
    return (
      <div className="mx-auto max-w-lg">
        <Alert tone="error" title="This feedback link is incomplete">
          Please open the full link from your email.
        </Alert>
      </div>
    );
  }
  if (info.isPending) return <Spinner />;
  if (info.isError) {
    return (
      <div className="mx-auto max-w-lg">
        <Alert tone="info" title="This link can’t be used">
          {info.error.message}
        </Alert>
      </div>
    );
  }
  if (submit.isSuccess) {
    return (
      <Card className="mx-auto max-w-lg p-8 text-center">
        <span className="mx-auto mb-4 flex size-14 items-center justify-center rounded-full bg-brand-soft text-brand">
          <CircleCheck className="size-7" aria-hidden />
        </span>
        <h1 className="text-xl font-bold">Thank you!</h1>
        <p className="mt-2 text-muted">Your feedback helps the IT team improve.</p>
        <ButtonLink to="/" variant="secondary" className="mt-6">
          Back to the helpdesk
        </ButtonLink>
      </Card>
    );
  }
  if (tapped && !alreadyRated && !quick.isSuccess && !quick.isError)
    return <Spinner label="Saving your rating…" />;
  return (
    <div className="mx-auto max-w-xl">
      {rated ? (
        <Alert tone="success" title={`Thank you! You rated us ${rated} out of 5.`} className="mb-4">
          That’s all we need. If you have a moment, the questions below help us improve.
        </Alert>
      ) : (
        <PageHeader title="How did we do?" description={`${info.data.number} · ${info.data.subject}`} />
      )}
      <Card className="p-5 sm:p-6">
        <FeedbackForm
          satisfaction={rated ?? undefined}
          onSubmit={(input) => submit.mutate(input)}
          pending={submit.isPending}
          serverError={submit.error instanceof ApiError ? submit.error.message : undefined}
        />
      </Card>
    </div>
  );
}
